
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import style from '../../styles/content.module.css'

import { getLayout, LayoutContext } from '../../components/sg/Layout'
import Header from '../../components/Header'
import useTranslation from 'next-translate/useTranslation'
import classNames from 'classnames/bind'
import Link from 'next/link'
import Image from 'next/image'
import { useLivePrice } from '../../hooks/usePrices'
import resolveProductUnit from '../../helpers/resolveProductUnit'
import Cards from '../../components/sg/Cards'
import Footer from '../../components/sg/Footer'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useContext } from 'react'

export default function SavingInGold () {
  const { t } = useTranslation('main')
  const c = classNames.bind(style)
  const router = useRouter()
  const { locale } = router
  const [layoutContext] = useContext(LayoutContext)
  const { currency } = layoutContext
  const { productPriceResolution } = resolveProductUnit('gram', 1)
  const { price: { value: spotPrice } } = useLivePrice(currency, 'D', productPriceResolution)

  return (
    <>
      <Head>
        <title>Top 5 reasons why every investor should buy some gold bullion - Gold Savings Account</title>
        <meta property='og:title' key='og:title' content='Top 5 reasons why every investor should buy some gold bullion' />
        <meta property='og:url' key='og:url' content={router?.pathname} />
        <meta property='og:image' key='og:image' content='https://goldsavingsaccount.sg/images/og_goldsavingsaccount.png' />
        <meta property='og:description' key='og:description' content='The best forms of money typically exhibit these unique characteristics' />
        <meta property='og:type' key='og:type' content='article' />
        <meta property='og:site_name' key='og:site_name' content={t('layout-goldsaver')} />
        <meta name='title' key='title' content='Top 5 reasons why every investor should buy some gold bullion' />
        <meta name='description' key='description' content='The best forms of money typically exhibit these unique characteristics' />
        <link rel='canonical' href='https://goldsavingsaccount.sg/zh/sg/why-should-i-save-in-gold' />
        <link rel='alternate' hreflang='x-default' href='https://goldsavingsaccount.sg/sg/why-should-i-save-in-gold' />
        <link rel='alternate' hreflang='en' href='https://goldsavingsaccount.sg/sg/why-should-i-save-in-gold' />
        <link rel='alternate' hreflang='zh' href='https://goldsavingsaccount.sg/zh/sg/why-should-i-save-in-gold-zh' />
      </Head>
      <Header showLogo />
      <div className={c('content')}>
        <div className={c('hero')}>
          <Image src='/images/goldbars.jpg' layout='fill' alt='Photo of gold bars' />
        </div>
        <div className={c('inner')}>
          <div className={c('single')}>
            <h3>Here are the top 5 reasons why every investor should buy some gold bullion.</h3>
            <h4>#1 – Gold is Money</h4>
            <p>In the annals of history, gold has been present in nearly every civilization. It was used as a medium of exchange as early as 2700 BC in Egypt. The Kingdom of Lydia (now a part of modern-day Turkey) was the first to strike gold coins in 550 BC. It was not by the decrees of kings or governments that the value of gold was recognized but because gold fulfilled the characteristics of money.</p>
            <p>The best forms of money typically exhibit the following six characteristics:</p>
            <ul>
              <li>Durable – it must not be destroyed easily from repeated use.</li>
              <li>Portable – it must be easy to carry around.</li>
              <li>Uniform – all versions of the same denomination must have the same purchasing power.</li>
              <li>Divisible – can be divided into smaller units of value.</li>
              <li>Fungible – one unit is as interchangeable with another.</li>
              <li>Relatively rare and intrinsically valuable</li>
            </ul>
            <p>Gold fulfills all these characteristics of money.</p>
            <p>Gold will not tarnish, rust, and it is the most corrosion-proof and oxidation-resistant metal. Its durability is why gold ornaments and coins continue to be unearthed by archaeology, polished, and displayed in museums despite being buried for centuries.</p>
            <p>Given that gold is one of the densest metals on Earth, a piece of gold packs considerable value in a small space, making it a highly portable store of value. At current prices, the value of a 1-kilogram gold bar that is slightly smaller than most smartphones in size is {(spotPrice * 1000 || 86000).toLocaleString(locale, { style: 'currency', currency: currency, currencyDisplay: 'code', maximumFractionDigits: 2, minimumFractionDigits: 2 })}.</p>
            <p>Gold refined to the same purity with the same weight has the same purchasing power. This uniformity allows gold to be divisible into smaller units that are interchangeable, making it an excellent medium of exchange.</p>
            <p>Gold’s history as money is also why global central banks continue to hold gold as reserves. When the value of fiat currencies is disputed or destroyed, gold has always been the ultimate fallback the world trusts.</p>

            <h4>#2 – Gold is a Rare Tangible Asset</h4>
            <p>Unlike fiat currencies, gold cannot be printed in unlimited quantities by governments. Considerable capital and productive effort are required to mine gold from the Earth’s crust. Gold mining is one of the riskiest industrial sectors as every stage from prospecting, discovery, mine building, extracting, and refining is fraught with risks. Only one out of thousands of gold discoveries becomes a mine, and this process alone takes 15 years before the successful opening of a mine.</p>
            <p>Gold is often referred to as a precious metal together with silver and platinum. It is precious because of its rarity in the Earth’s crust. Gold only has a crustal abundance of 0.004 parts per million (ppm). Comparatively, there are 15,000 times more copper and 21,000 times more nickel in the Earth’s crust – no wonder gold is precious!</p>

            <h4>#3 – Gold Protects Your Purchasing Power</h4>
            <p>Purchasing power is the number of goods and services that a unit of currency can purchase. The U.S. dollar, which is the world’s reserve currency, has lost over 99% of its purchasing power since 1913 – the year the Federal Reserve was created. Therefore, one dollar in 2020 is only worth one cent in 1913 terms, representing an average yearly loss of 3.2%. As more U.S. dollars are created out of thin air by the Federal Reserve, the purchasing power of each dollar will continue to fall. </p>
            <p>Gold, on the other hand, is an excellent store of wealth that maintains its value over time. The price of gold has appreciated from USD 18.92 per troy ounce in 1913 to over USD 2,000 in 2020, gaining 4.4% annually.  </p>
            <p>Even when we look into history beyond 1913, an ounce of gold could buy a toga, formal wear in Roman times, and it can still buy a nice suit today. Gold is, therefore, a natural defense against money printing by governments and is a crucial asset in every portfolio.</p>

            <h4>#4 – Gold is a Low-Risk Asset That is Easy to Understand</h4>
            <p>Gold is one of the safest assets available today. Physical gold has no counterparty risk. Unlike fiat currencies or many other financial assets, gold bullion is not a promise of value – it is the embodiment of value itself! The gold bars or coins you own cannot go bankrupt or default on obligations.</p>
            <p>Owning gold is simple and does not require any specialized knowledge. In its simplest form, gold bullion is bought, stored safely, and taken out when it is time to sell. Given that gold is a globally traded asset, its value is transparent and easily found on the Internet and the websites of bullion dealers.</p>

            <h4>#5 – Gold is a Hard Asset That is Highly Liquid </h4>
            <p>While risks exist for the value of stocks, bonds, and real estate to go to zero, the price of gold has never gone to zero in the history of humankind. Gold, being money, is always in demand when the economy is booming or in recession. It is highly liquid as there is no lack of buyers or sellers for gold.</p>
            <p>Owners of gold can sell their gold anytime should they deem the prevailing gold price advantageous. In this digital age, it is common for gold owners to sell gold bullion online with dealers 24/7. Determining the price to sell is often transparent with the submission of sell orders online. The same cannot be said of other hard assets such as real estate, as they may require a more extended period to find buyers.</p>
            <p>Gold, being highly liquid, allows you to be agile in managing your portfolio depending on developments in the market.</p>
            <br />
            <p>You can begin to protect your savings from inflation and uncertainty today with our Gold Savings Account by signing up below.</p>
          </div>
          <div className={c('promotion')}>
            {t('banner-mission-1')}<br />
            {t('banner-mission-2')}<br />
            <br />
            <br />
            <Link href='/signup'>
              <button className='goldenshimmer' title={t('banner-signup-button-title')}>{t('banner-signup-button')}</button>
            </Link>
            <span className={c('termsnote')}>{t('banner-signup-subtext')}</span>
          </div>
        </div>
      </div>
      <Cards />
      <Footer />
    </>
  )
}

SavingInGold.getLayout = getLayout


    export async function getStaticProps(ctx) {
        
        
        return {
          
          
          props: {
            
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/sg/why-should-i-save-in-gold',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  